import '@dcs/ui/primitives.css';
import type { ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router';
import { t } from '../messages/vi';
import { AppHeader } from './app-header';
import { TabBar } from './app-nav';
import { Loading } from './loading';
import { RouteError } from './route-error';
import './app-layout.css';

/** Full-screen routes (3D drive and exam): they draw over the whole viewport, so no frame. */
const IMMERSIVE = ['/drive', '/exam'] as const;

export const isImmersive = (pathname: string) =>
  IMMERSIVE.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

/** Id of the landmark the skip link jumps to. */
export const MAIN_ID = 'main';

/**
 * The app frame: skip link, sticky header (name, "Điều hướng chính" from 768px, account),
 * `<main id="main">` around the screen, and the tab bar under 768px. On `/drive*` and `/exam*`
 * the screen is drawn alone.
 */
function AppFrame({ children }: { readonly children: ReactNode }) {
  const { pathname } = useLocation();
  if (isImmersive(pathname)) return children;
  return (
    <div className="app-shell">
      <a className="skip-link" href={`#${MAIN_ID}`}>
        {t('layout.skipToMain')}
      </a>
      <AppHeader />
      <main id={MAIN_ID} className="app-shell__main" tabIndex={-1}>
        {children}
      </main>
      <TabBar />
    </div>
  );
}

/** Layout route of every screen. */
export function AppLayout() {
  return (
    <AppFrame>
      <Outlet />
    </AppFrame>
  );
}

/**
 * Error boundary of the layout route: an unknown path (404) or a screen that throws shows the
 * error screen inside the same frame, so the header and the tab bar stay usable.
 */
export function AppErrorLayout() {
  const { pathname } = useLocation();
  return (
    <AppFrame>
      <RouteError frameless={isImmersive(pathname)} />
    </AppFrame>
  );
}

/** Hydrate fallback of the layout route: a skeleton the size of the screen that is loading. */
export function LoadingScreen() {
  const { pathname } = useLocation();
  return <Loading framed={!isImmersive(pathname)} />;
}
